import { type ComputedRef, computed, ref } from 'vue'

import type { GalleryImage } from '@/types/images'

/**
 * Composable for image gallery display logic
 *
 * Determines presentation state like whether multiple images exist
 * and if cropping should be disabled
 *
 * @param images - Array or ComputedRef of images
 */
export function useImageGallery(images: ComputedRef<GalleryImage[]> | GalleryImage[]) {
  const imageArray = computed(() => (Array.isArray(images) ? images : images.value))

  const hasMultipleImages = computed(() => imageArray.value.length > 1)

  const noCroppingAllowed = computed(() => imageArray.value.length === 1 && imageArray.value[0]?.noCrop === true)

  return {
    hasMultipleImages,
    noCroppingAllowed,
  }
}

interface UseImageErrorHandlingOptions {
  /**
   * Optional callback when an image fails to load
   */
  onError?: (failedImage: GalleryImage, remainingImages: GalleryImage[]) => void
}

/**
 * Composable for handling image load errors
 *
 * Tracks failed images and provides filtered list of valid images
 *
 * @param images - Function that returns array of images to track
 * @param options - Optional callbacks and configuration
 */
export function useImageErrorHandling(images: () => GalleryImage[], options: UseImageErrorHandlingOptions = {}) {
  const imageErrors = ref<Set<string>>(new Set())

  const validImages = computed(() => images().filter((img) => !imageErrors.value.has(img.src)))

  const handleImageError = (image: GalleryImage) => {
    imageErrors.value.add(image.src)

    if (options.onError) {
      options.onError(image, validImages.value)
    }
  }

  return {
    validImages,
    handleImageError,
  }
}
